import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";
import api from "@/lib/api";
import { ALL_FUNNELS } from "@/lib/funnelOptions";
import { FunnelPicker } from "@/components/funnels/FunnelPicker";
import { BuildPagesDialog } from "@/components/landers/BuildPagesDialog";
import { BTN_BLACK, BTN_WHITE, errText } from "@/components/pageSettings/AdminModal";
import { PageLinks } from "./PageLinks";
import { StepShell, setupUrl } from "./StepShell";

function OwnPages({ pages, onBuild }) {
  return (
    <div data-testid="setup-own-pages">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="label-mono">Their own pages</div>
        <button type="button" className={`${BTN_WHITE} ml-auto`} onClick={onBuild} data-testid="setup-build-pages">
          <Plus size={11} weight="bold" /> Build their own pages
        </button>
      </div>
      {pages.length ? (
        <ul className="mt-2 border-2 border-black divide-y divide-zinc-200">
          {pages.map((p) => (
            <li key={p.id} className="flex items-center gap-2 px-3 py-2 text-xs" data-testid={`setup-own-page-${p.slug}`}>
              <span className="flex-1 min-w-0 truncate">{p.name || p.slug}</span>
              <span className={`label-mono ${p.status === "published" ? "text-emerald-700" : "text-zinc-500"}`}>
                {p.status === "published" ? "Published" : "Draft"}
              </span>
              <Link to={`/admin/landing-pages/custom/${p.id}`} className={BTN_WHITE} data-testid={`setup-own-page-edit-${p.slug}`}>Edit</Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[11px] text-zinc-500 mt-1">Optional. Pages with their own name and copy. They start as drafts; publish each one from its editor.</p>
      )}
    </div>
  );
}

export function PagesStep({ state, orgId, onSaved, carry }) {
  const [picks, setPicks] = useState(state.profile?.picks?.length ? state.profile.picks : carry.prefill?.funnels || ALL_FUNNELS);
  const [building, setBuilding] = useState(false);
  const [busy, setBusy] = useState(false);
  const reload = () => api.get(setupUrl(orgId)).then((r) => onSaved(r.data));
  const save = async () => {
    if (!picks.length) { toast.error("Pick at least one shared page"); return; }
    setBusy(true);
    try {
      const { data } = await api.put(`${setupUrl(orgId)}/pages`, { picks });
      toast.success("Landing pages saved");
      onSaved(data, "delivery");
    } catch (e) {
      toast.error(errText(e, "Couldn't save the pages"));
    } finally {
      setBusy(false);
    }
  };
  if (!state.profile) {
    return <StepShell title="Landing pages" testId="setup-step-pages" hint="Create their buyer page in step 1 first." />;
  }
  return (
    <StepShell title="Landing pages" testId="setup-step-pages"
               hint="The shared pages that can send them leads, plus any pages of their own. Leads only flow after you go live."
               footer={<button type="button" className={BTN_BLACK} onClick={save} disabled={busy} data-testid="setup-pages-save">{busy ? "Saving…" : "Save & continue"}</button>}>
      <div>
        <div className="label-mono mb-1">Shared pages that can send them leads *</div>
        <FunnelPicker value={picks} onChange={setPicks} testId="setup-funnels" />
      </div>
      <OwnPages pages={state.own_pages} onBuild={() => setBuilding(true)} />
      <PageLinks links={state.links} live={state.live} />
      {building && <BuildPagesDialog b={{ id: state.profile.id, firm_name: state.profile.firm_name || state.org.name }} onClose={() => setBuilding(false)} onBuilt={reload} />}
    </StepShell>
  );
}
